import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { MotionConfig, motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getNameErrorMessage } from "./api/saveErrors";
import {
  canCheckOverrideImpact,
  overrideImpactQueryOptions,
} from "./api/useOverrideImpact";
import { useSaveSegment } from "./api/useSaveSegment";
import { useSegmentCounts } from "./api/useSegmentCounts";
import { AffectedOverridesDialog } from "./components/AffectedOverridesDialog";
import { ModeSwitch } from "./components/ModeSwitch";
import { SegmentBreadcrumbs } from "./components/SegmentBreadcrumbs";
import { SegmentBuilderTabs } from "./components/SegmentBuilderTabs";
import { SegmentFooter } from "./components/SegmentFooter";
import { SegmentNameField } from "./components/SegmentNameField";
import { SEGMENT_MODE, TAB_PARAM, type SegmentMode } from "./constants";
import {
  findFirstInvalidConditionKey,
  hasInvalidLogic,
  INVALID_LOGIC_RADIO_SELECTOR,
} from "./errors";
import { useBuilderDraft } from "./hooks/useBuilderDraft";
import { useSegmentPaths } from "./hooks/useSegmentPaths";
import { UNDO_TOAST_DURATION_MS } from "./hooks/useUndoToast";
import {
  createEmptySegmentValues,
  toDraftBody,
  toFormValues,
  toSaveRequest,
} from "./mappers";
import {
  getAffectedRows,
  removeOverrides,
  toOverrideRows,
  type OverrideRow,
} from "./overrides";
import { useSegmentFormSchema, type SegmentFormValues } from "./schemas";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { segmentDetailQueryOptions } from "@/features/segments/api/useSegmentDetail";
import { ArchiveSegmentAction } from "@/features/segments/components/ArchiveSegmentAction";
import { DuplicateSegmentAction } from "@/features/segments/components/DuplicateSegmentAction";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import { SegmentTourHost } from "@/features/segments/tour/SegmentTourHost";
import { useSegmentTourDemo } from "@/features/segments/tour/useSegmentTourDemo";
import type {
  SaveSegmentRequest,
  SegmentDetailResponse,
  SegmentField,
  VacancyOverride,
} from "@/features/segments/types";
import { TourHelpMenu } from "@/features/tours/components/TourHelpMenu";
import { useTour } from "@/features/tours/useTour";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { usePrompt } from "@/lib/hooks/usePrompt";
import useToast from "@/lib/hooks/useToast";

type SegmentBuilderFormProps = {
  mode: SegmentMode;
  segment?: SegmentDetailResponse;
  segmentId?: string;
  clientId?: string;
  fields: SegmentField[];
  fadeIn?: boolean;
};

type PendingSave = {
  request: SaveSegmentRequest;
  affected: OverrideRow[];
};

const COUNT_DEBOUNCE_MS = 400;
const MODE_FADE_TRANSITION = { duration: 0.15 } as const;

export function SegmentBuilderForm({
  mode,
  segment,
  segmentId,
  clientId,
  fields,
  fadeIn = true,
}: SegmentBuilderFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paths = useSegmentPaths();
  const { showToast, dismissToast } = useToast();
  const queryClient = useQueryClient();
  const { canCreate, canEdit } = usePermissionMatrix();
  const { isRunning: isTourRunning, stop: stopTour } = useTour();
  const archiveRef = useTourTarget<HTMLDivElement>(SEGMENT_TOUR_TARGET.ARCHIVE);
  const schema = useSegmentFormSchema(fields);
  const readOnly = mode === SEGMENT_MODE.VIEW;
  const [searchParams, setSearchParams] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const pendingLogicFocus = useRef(false);
  const [activeConditionKey, setActiveConditionKey] = useState<string | null>(
    null,
  );

  const [defaultValues] = useState(() =>
    segment ? toFormValues(segment) : createEmptySegmentValues(),
  );
  const methods = useForm<SegmentFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });
  const tourDemo = useSegmentTourDemo(methods, fields, readOnly);

  const values = methods.watch();
  const counts = useSegmentCounts({
    clientId,
    values,
    debounceMs: readOnly ? 0 : COUNT_DEBOUNCE_MS,
  });
  const draft = useBuilderDraft({
    segmentId,
    clientId,
    fields,
    values,
    savedValues: defaultValues,
    debounceMs: readOnly ? 0 : COUNT_DEBOUNCE_MS,
  });
  const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);
  const affectedCount = readOnly ? 0 : draft.overrides.affected.length;

  useEffect(() => {
    if (readOnly && segment) methods.reset(toFormValues(segment));
  }, [readOnly, segment, methods]);
  const save = useSaveSegment({ segmentId });
  const { isDirty } = methods.formState;
  const { markSaved, confirmDialog } = usePrompt(
    {
      title: t("prompts.unsavedChanges.title"),
      description: t("prompts.unsavedChanges.description"),
    },
    isDirty,
  );

  const action = segmentId ? "updated" : "created";

  const { formState, getValues } = methods;
  const { submitCount } = formState;
  const handledSubmitCount = useRef(0);

  const clearTabParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(TAB_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (submitCount === handledSubmitCount.current) return;
    handledSubmitCount.current = submitCount;
    if (formState.errors.name) return;
    const values = getValues();
    const invalidKey = findFirstInvalidConditionKey(formState.errors, values);
    if (!invalidKey && !hasInvalidLogic(formState.errors, values)) return;
    clearTabParam();
    if (invalidKey) {
      setActiveConditionKey(invalidKey);
      return;
    }
    pendingLogicFocus.current = true;
  }, [submitCount, formState, getValues, clearTabParam]);

  useEffect(() => {
    if (isTourRunning) setActiveConditionKey(null);
  }, [isTourRunning]);

  useEffect(() => {
    if (!pendingLogicFocus.current) return;
    const radio = formRef.current?.querySelector<HTMLElement>(
      INVALID_LOGIC_RADIO_SELECTOR,
    );
    if (!radio) return;
    pendingLogicFocus.current = false;
    radio.focus();
  }, [searchParams, formState.errors]);

  const restoreRemovedOverrides = async (
    savedId: string,
    removed: VacancyOverride[],
  ) => {
    const latest = await queryClient.fetchQuery({
      ...segmentDetailQueryOptions({ segmentId: savedId, clientId }),
      staleTime: 0,
    });
    const { data: request, error } = toSaveRequest(
      toFormValues(latest),
      clientId,
      fields,
    );
    if (error) throw error;
    const removedIds = removed.map((override) => override.vacancyId);
    const vacancyOverrides = [
      ...removeOverrides(request.vacancyOverrides, removedIds),
      ...removed,
    ];
    await save.mutateAsync({
      request: { ...request, vacancyOverrides },
      segmentId: savedId,
    });
  };

  const showRemovedOverridesToast = (
    savedId: string,
    removed: VacancyOverride[],
  ) => {
    const toastId = crypto.randomUUID();
    showToast(
      t("admin.segments.segmentAction.success", { action }),
      "success",
      {
        id: toastId,
        duration: UNDO_TOAST_DURATION_MS,
        description: t("admin.segments.builder.exceptions.removedToast", {
          count: removed.length,
        }),
        action: {
          label: t("admin.segments.builder.undo"),
          onClick: () => {
            dismissToast(toastId);
            restoreRemovedOverrides(savedId, removed)
              .then(() =>
                showToast(
                  t("admin.segments.builder.exceptions.restoredToast"),
                  "success",
                ),
              )
              .catch((error) => {
                console.error("[segments] undo removed overrides:", error);
                showToast(
                  t("admin.segments.segmentAction.error", {
                    action: "updated",
                  }),
                  "error",
                );
              });
          },
        },
      },
    );
  };

  const submit = (
    request: SaveSegmentRequest,
    removedVacancyIds: number[] = [],
  ) => {
    const saved = {
      ...request,
      vacancyOverrides: removeOverrides(
        request.vacancyOverrides,
        removedVacancyIds,
      ),
    };
    const removed = request.vacancyOverrides.filter((override) =>
      removedVacancyIds.includes(override.vacancyId),
    );
    save.mutate(
      { request: saved },
      {
        onSuccess: (savedId) => {
          setPendingSave(null);
          markSaved();
          if (removed.length > 0) {
            showRemovedOverridesToast(savedId, removed);
          } else {
            showToast(
              t("admin.segments.segmentAction.success", { action }),
              "success",
            );
          }
          navigate(paths.view(savedId), { replace: true });
        },
        onError: (error) => {
          const nameError = getNameErrorMessage(t, error);
          if (!nameError) {
            showToast(
              t("admin.segments.segmentAction.error", { action }),
              "error",
            );
            return;
          }
          setPendingSave(null);
          methods.setError("name", { message: nameError });
        },
      },
    );
  };

  const loadAffectedOverrides = async (
    formValues: SegmentFormValues,
  ): Promise<OverrideRow[]> => {
    const body = toDraftBody(formValues, clientId);
    if (!canCheckOverrideImpact(body)) return [];
    const impact = await queryClient.fetchQuery(
      overrideImpactQueryOptions(segmentId, body),
    );
    return getAffectedRows(
      toOverrideRows(impact, body.vacancyOverrides),
      draft.overrides.kept,
    );
  };

  const handleSave = methods.handleSubmit(async (formValues) => {
    const { data: request, error } = toSaveRequest(
      formValues,
      clientId,
      fields,
    );
    if (error) {
      console.error("[segments] save request after validation:", error);
      showToast(t("admin.segments.segmentAction.error", { action }), "error");
      return;
    }

    const affected = await loadAffectedOverrides(formValues).catch((error) => {
      console.error("[segments] override impact before save:", error);
      return null;
    });
    if (affected === null) {
      showToast(t("admin.segments.segmentAction.error", { action }), "error");
      return;
    }
    if (affected.length > 0) {
      setPendingSave({ request, affected });
      return;
    }
    submit(request);
  });

  const handleCancel = () =>
    navigate(segmentId ? paths.view(segmentId) : paths.overview);

  return (
    <MotionConfig reducedMotion="user">
      <FormProvider {...methods}>
        <ModernPageLayout
          breadcrumbs={
            <SegmentBreadcrumbs
              current={segment?.name ?? t("admin.segments.create")}
            />
          }
          title={t("admin.segments.builder.title")}
          subtitle={t("admin.segments.builder.subtitle")}
          backbutton={{
            label: t("common.backTo", { entity: t("common.segments") }),
            href: segmentId
              ? paths.overviewSelected(segmentId)
              : paths.overview,
          }}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <TourHelpMenu />
              {segment && canCreate("segments") && (
                <DuplicateSegmentAction
                  segment={segment}
                  clientId={clientId}
                  variant="button"
                  disabledReason={
                    isDirty
                      ? t("admin.segments.duplicate.saveFirst")
                      : undefined
                  }
                  onDuplicated={stopTour}
                />
              )}
              {segmentId && canEdit("segments") && (
                <ModeSwitch mode={mode} segmentId={segmentId} />
              )}
            </div>
          }
        >
          <form ref={formRef} onSubmit={handleSave} noValidate>
            <motion.div
              initial={fadeIn ? { opacity: 0 } : false}
              animate={{ opacity: 1 }}
              transition={MODE_FADE_TRANSITION}
              className="flex flex-col gap-6"
            >
              <SegmentNameField
                readOnly={readOnly}
                status={segment?.status}
                autoFocus={!segmentId}
              />
              <SegmentBuilderTabs
                segmentId={segmentId}
                clientId={clientId}
                fields={fields}
                readOnly={readOnly}
                counts={counts}
                draft={draft}
                activeConditionKey={
                  isTourRunning ? tourDemo.conditionKey : activeConditionKey
                }
                onActiveConditionChange={setActiveConditionKey}
                tourDemoControl={tourDemo.control}
              />
              {!readOnly && (
                <SegmentFooter
                  isDirty={isDirty}
                  isSaving={save.isPending || methods.formState.isSubmitting}
                  affectedCount={affectedCount}
                  onCancel={handleCancel}
                  secondaryAction={
                    segment && (
                      <div ref={archiveRef}>
                        <ArchiveSegmentAction
                          segment={segment}
                          clientId={clientId}
                          variant="button"
                          onArchived={() => {
                            markSaved();
                            navigate(paths.overview);
                          }}
                        />
                      </div>
                    )
                  }
                />
              )}
            </motion.div>
          </form>
        </ModernPageLayout>
        {confirmDialog}
        {pendingSave && (
          <AffectedOverridesDialog
            segmentName={values.name}
            affected={pendingSave.affected}
            ruleChanges={draft.ruleChanges}
            isSaving={save.isPending}
            onCancel={() => setPendingSave(null)}
            onSaveWithoutFixing={() => submit(pendingSave.request)}
            onFixAndSave={(vacancyIds) =>
              submit(pendingSave.request, vacancyIds)
            }
          />
        )}
        <SegmentTourHost
          mode={mode}
          segmentId={segmentId}
          readOnly={readOnly}
          segmentCount={draft.listCounts.data?.includedCount}
          fields={fields}
          onStepChange={tourDemo.applyStep}
        />
      </FormProvider>
    </MotionConfig>
  );
}
